import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import './AllDocents.css';
import { formatDate } from '../../utilities.js';
import LocationFilter from '../locaties/LocationFilter.js';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import LifecycleActionButtons from '../../components/LifecycleActionButtons.js';
import {
  confirmLifecycleAction,
  showLifecycleSuccess,
  showLifecycleError
} from '../../utils/confirmLifecycleAction.js';
import { getStatusBadgeInfo } from '../../utils/lifecycleUtils.js';

function AllDocents() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem('jwtToken');
  const { userInfo, isAuthenticated } = useAuth();
  const userRole = userInfo?.role;
  const is_single_user = userInfo?.is_single_user;

  const [docents, setDocents] = useState([]);
  const [zoekterm, setZoekterm] = useState('');
  const [debouncedZoekterm, setDebouncedZoekterm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState('all');
  const [showInactive, setShowInactive] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false
  });

  axios.defaults.withCredentials = true;

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedZoekterm(zoekterm.trim());
    }, 400);

    return () => clearTimeout(timer);
  }, [zoekterm]);

  const fetchDocents = useCallback(() => {
    setIsLoading(true);

    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/docent/allDocent`, {
        params: {
          page,
          limit,
          search: debouncedZoekterm,
          location_id: locationFilter,
          showInactive,
          showArchived
        },
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((response) => {
        setDocents(response.data.Data || []);
        setPagination(
          response.data.pagination || {
            page: 1,
            limit,
            total: 0,
            totalPages: 1,
            hasNextPage: false,
            hasPrevPage: false
          }
        );
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching docenten', error);
        setDocents([]);
        setPagination({
          page: 1,
          limit,
          total: 0,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false
        });
        setIsLoading(false);
      });
  }, [token, page, limit, debouncedZoekterm, locationFilter, showInactive, showArchived]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/location/allLocations`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then((res) => {
        const locs = res.data.data || res.data;
        setLocations(Array.isArray(locs) ? locs : []);
      })
      .catch((err) => {
        console.error('Error fetching locations', err);
        setLocations([]);
      });
  }, [token]);

  useEffect(() => {
    fetchDocents();
  }, [fetchDocents]);

  const locationMap = useMemo(() => {
    const map = {};
    locations.forEach((loc) => {
      const id = loc.location_id;
      const name = loc.name || loc.location_name || loc.locatie_naam;
      if (id) {
        map[id] = name || id;
      }
    });
    return map;
  }, [locations]);

  const getLocationNames = (docent) => {
    const ids = Array.isArray(docent.location_ids) ? docent.location_ids : [];
    const validIds = ids.filter((id) => id && typeof id === 'string');

    if (validIds.length === 0) return 'Geen locatie gekoppeld';

    return validIds
      .map((id) => locationMap[id] || id)
      .join(', ');
  };

  const handleViewDocent = (id) => navigate(`/viewdocent/${id}`);

  const handleLifecycleAction = async (docentId, action, entityLabel) => {
    const { isConfirmed } = await confirmLifecycleAction(action, entityLabel);
    if (!isConfirmed) return;

    setActionLoadingId(docentId);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/docent/${docentId}/${action}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      await showLifecycleSuccess(
        action,
        entityLabel,
        res.data?.message || ''
      );

      fetchDocents();
    } catch (error) {
      console.error(`Error during docent lifecycle action ${action}`, error);
      await showLifecycleError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          `Actie mislukt: ${action}`
      );
    } finally {
      setActionLoadingId(null);
    }
  };

  const getStatusBadge = (docent) => {
    const badge = getStatusBadgeInfo(docent);
    return <span className={badge.className}>{badge.label}</span>;
  };

  const handleLimitChange = (e) => {
    setLimit(Number(e.target.value));
    setPage(1);
  };

  const renderPaginationButtons = () => {
    if ((pagination.totalPages || 1) <= 1) return null;

    const currentPage = pagination.page || 1;
    const totalPages = pagination.totalPages || 1;

    let startPage = Math.max(1, currentPage - 2);
    let endPage = Math.min(totalPages, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages, 5);
    }

    if (currentPage >= totalPages - 1) {
      startPage = Math.max(1, totalPages - 4);
    }

    const buttons = [];

    for (let i = startPage; i <= endPage; i += 1) {
      buttons.push(
        <button
          key={i}
          type="button"
          className={`btn btn-sm ${i === currentPage ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => setPage(i)}
        >
          {i}
        </button>
      );
    }

    return buttons;
  };

  const startRecord = pagination.total === 0 ? 0 : (pagination.page - 1) * pagination.limit + 1;
  const endRecord =
    pagination.total === 0
      ? 0
      : Math.min(pagination.page * pagination.limit, pagination.total);

  return (
    <div className="all-docent">
      {isAuthenticated ? (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h1>Overzicht Van Alle Docenten</h1>
            <Link to="/newdocent" className="btn btn-success">
              <i className="bi bi-plus-circle me-2"></i>Nieuwe docent
            </Link>
          </div>

          {!is_single_user && (
            <div className="mb-3">
              <LocationFilter
                userRole={userRole}
                locationFilter={locationFilter}
                setLocationFilter={(value) => {
                  setLocationFilter(value);
                  setPage(1);
                }}
                locations={locations}
              />
            </div>
          )}

          <div className="row mb-3">
            <div className="col-12 col-md-4 mb-2">
              <input
                type="text"
                className="form-control rounded-0"
                placeholder="Zoek op voornaam, achternaam, email of mobiel"
                value={zoekterm}
                onChange={(e) => {
                  setZoekterm(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="col-12 col-md-2 mb-2">
              <select
                className="form-select rounded-0"
                value={limit}
                onChange={handleLimitChange}
              >
                <option value={10}>Toon 10</option>
                <option value={20}>Toon 20</option>
                <option value={50}>Toon 50</option>
                <option value={100}>Toon 100</option>
              </select>
            </div>

            <div className="col-12 col-md-6 d-flex flex-column flex-md-row gap-3 align-items-md-center">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showInactiveDocents"
                  checked={showInactive}
                  onChange={(e) => {
                    setShowInactive(e.target.checked);
                    setPage(1);
                  }}
                />
                <label className="form-check-label" htmlFor="showInactiveDocents">
                  Toon inactieve docenten
                </label>
              </div>

              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="showArchivedDocents"
                  checked={showArchived}
                  onChange={(e) => {
                    setShowArchived(e.target.checked);
                    setPage(1);
                  }}
                />
                <label className="form-check-label" htmlFor="showArchivedDocents">
                  Toon gearchiveerde docenten
                </label>
              </div>

              <button
                type="button"
                className="btn btn-outline-secondary btn-sm"
                onClick={() => {
                  setZoekterm('');
                  setLocationFilter('all');
                  setShowInactive(false);
                  setShowArchived(false);
                  setLimit(20);
                  setPage(1);
                }}
              >
                Filters wissen
              </button>
            </div>
          </div>

          <div className="row mb-3 align-items-center">
            <div className="col-12">
              <small className="text-muted">
                Toont {startRecord} t/m {endRecord} van {pagination.total || 0} docenten
              </small>
            </div>
          </div>

          {isLoading ? (
            <p>Loading...</p>
          ) : docents.length === 0 ? (
            <p>Geen docenten gevonden</p>
          ) : (
            <>
              <div className="docent-cards">
                {docents.map((docent) => (
                  <div
                    key={docent.docent_id}
                    className="docent-card"
                    onClick={() => handleViewDocent(docent.docent_id)}
                  >
                    <div className="docent-card-header">
                      <div className="docent-card-info">
                        <div className="docent-card-name">
                          {docent.firstname} {docent.lastname}
                        </div>
                        <div className="docent-card-email">{docent.email}</div>

                        <div className="docent-card-location mt-1">
                          <i className="bi bi-geo-alt me-1"></i>
                          {getLocationNames(docent)}
                        </div>

                        <div className="mt-1">{getStatusBadge(docent)}</div>
                      </div>

                      {docent.mobile && (
                        <a
                          href={`tel:${docent.mobile}`}
                          className="btn btn-sm btn-outline-secondary rounded-circle"
                          style={{
                            width: 34,
                            height: 34,
                            padding: 0,
                            lineHeight: '32px',
                            textAlign: 'center',
                            flexShrink: 0
                          }}
                          onClick={(e) => e.stopPropagation()}
                          title={docent.mobile}
                        >
                          <i className="bi bi-telephone" style={{ fontSize: '13px' }}></i>
                        </a>
                      )}
                    </div>

                    <div className="docent-card-meta">
                      {docent.birthdate && (
                        <span>
                          <i className="bi bi-cake me-1"></i>
                          {formatDate(docent.birthdate)}
                        </span>
                      )}
                      {docent.datein && (
                        <span>
                          <i className="bi bi-calendar-check me-1"></i>
                          In: {formatDate(docent.datein)}
                        </span>
                      )}
                      {docent.dateout && (
                        <span>
                          <i className="bi bi-calendar-x me-1"></i>
                          Uit: {formatDate(docent.dateout)}
                        </span>
                      )}
                    </div>

                    <div className="docent-card-actions" onClick={(e) => e.stopPropagation()}>
                      <LifecycleActionButtons
                        item={docent}
                        itemId={docent.docent_id}
                        entityLabel="docent"
                        editPath={`/editdocent/${docent.docent_id}`}
                        loadingId={actionLoadingId}
                        onAction={handleLifecycleAction}
                      />
                    </div>
                  </div>
                ))}
              </div>

              {(pagination.totalPages || 1) > 1 && (
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2 mt-4">
                  <div className="d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!pagination.hasPrevPage}
                      onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    >
                      Vorige
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      disabled={!pagination.hasNextPage}
                      onClick={() => setPage((prev) => prev + 1)}
                    >
                      Volgende
                    </button>
                  </div>

                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    {renderPaginationButtons()}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      ) : (
        <p>You are not authenticated.</p>
      )}
    </div>
  );
}

export default AllDocents;